import { isValidPhoneNumber, type CountryCode } from 'libphonenumber-js';

interface PhoneValidationMessages {
    emptyMessage?: string;
    invalidMessage?: string;
}

/**
 * Normalizes an app country code (typically lowercase ISO 3166-1 alpha-2)
 * to the uppercase form expected by libphonenumber-js.
 */
export function toPhoneCountryCode(countryCode?: string | null): CountryCode {
    return (countryCode || 'ca').toUpperCase() as CountryCode;
}

/** Digits-only national number for storage / validation. */
export function digitsOnly(value: string): string {
    return value.replace(/\D/g, '');
}

/**
 * Returns whether a national phone number is valid for the given country.
 * Empty strings are invalid — callers that treat blank as optional should
 * check for emptiness before calling this.
 */
export function isValidPhone(nationalNumber: string, countryCode?: string | null): boolean {
    const digits = digitsOnly(nationalNumber);
    if (!digits) {
        return false;
    }

    try {
        return isValidPhoneNumber(digits, toPhoneCountryCode(countryCode));
    } catch {
        return false;
    }
}

/**
 * Returns an error message for an invalid/empty national number, or '' when valid.
 */
export function getPhoneValidationError(
    nationalNumber: string,
    countryCode?: string | null,
    messages: PhoneValidationMessages = {},
): string {
    const digits = digitsOnly(nationalNumber);
    if (!digits) {
        return messages.emptyMessage ?? 'Please enter cell number';
    }
    if (!isValidPhone(digits, countryCode)) {
        return messages.invalidMessage ?? 'Enter a valid cell number';
    }
    return '';
}
